




























































import { mapGetters } from 'vuex';

import {
  getLimitsConfiguration,
  updateLimitsConfiguration
} from '@/api/CopyStake/Settings';
import {
  ECopyStakePackageProgram,
  SelectItem,
  TCopyStakeSettingsLimitsResponse,
  ValidationRules
} from '@/api/schema';

import { errorToastMessage } from '@/helpers/errorToastMessage';
import * as vr from '@/helpers/validation';
import { cloneDeep, isEqual } from 'lodash';
import type { AxiosError } from 'axios';

export default {
  name: 'CopyStakeSettingsLimits',
  data(): any {
    return {
      ECopyStakePackageProgram,

      limitsSavedData: {
        streamers: '',
        watchers: ''
      },
      limits: {
        streamers: '',
        watchers: ''
      },

      program: null,

      isLoading: false,
      formValid: false
    };
  },
  props: {
    title: {
      type: String,
      default: (): string => ''
    },
    data: {
      type: Object,
      default: null
    }
  },
  computed: {
    ...mapGetters('Onboarding', ['operatorId']),

    canEdit(): boolean {
      return this.$role.can.update('copy_stake streamers');
    },

    areLimitsFieldsEditable(): boolean {
      return this.program === ECopyStakePackageProgram.ENTERPRISE;
    },

    limitsChanged(): boolean {
      return !isEqual(this.limits, this.limitsSavedData);
    },
    valueCanBeSaved(): boolean {
      return this.formValid && this.limitsChanged;
    },

    programs(): SelectItem[] {
      return [
        {
          text: 'Mini Provision',
          value: ECopyStakePackageProgram.MINI_PROVISION
        },
        {
          text: 'Standard Provision',
          value: ECopyStakePackageProgram.STANDARD_PROVISION
        },
        {
          text: 'Enterprise',
          value: ECopyStakePackageProgram.ENTERPRISE
        }
      ];
    },
    programsSettingMap(): Record<
      ECopyStakePackageProgram,
      {
        streamers: number | null;
        watchers: number | null;
      }
    > {
      return {
        [ECopyStakePackageProgram.MINI_PROVISION]: {
          streamers: 2,
          watchers: 500
        },
        [ECopyStakePackageProgram.STANDARD_PROVISION]: {
          streamers: 5,
          watchers: 2000
        },
        [ECopyStakePackageProgram.ENTERPRISE]: {
          streamers: null,
          watchers: null
        }
      };
    },

    rules(): ValidationRules {
      return {
        streamersLimit: [
          vr.required,
          vr.validNumber,
          vr.integer,
          vr.positiveNumber
        ],
        watchersLimit: [
          vr.required,
          vr.validNumber,
          vr.integer,
          vr.positiveNumber
        ]
      };
    }
  },
  watch: {
    operatorId: {
      handler: 'getConfiguration',
      immediate: true
    }
  },
  methods: {
    changeProgram(program: ECopyStakePackageProgram): void {
      const { streamers, watchers } = this.programsSettingMap[program];

      this.limits.streamers = `${
        streamers ?? this.limitsSavedData.streamers ?? 0
      }`;
      this.limits.watchers = `${
        watchers ?? this.limitsSavedData.watchers ?? 0
      }`;

      if (program === ECopyStakePackageProgram.ENTERPRISE) {
        if (
          Object.values(this.programsSettingMap).some(
            ({ streamers, watchers }) =>
              this.limitsSavedData.streamers === String(streamers ?? '') &&
              this.limitsSavedData.watchers === String(watchers ?? '')
          )
        ) {
          this.limits.streamers = 0;
          this.limits.watchers = 0;
        }
      }
      this.$refs.form?.resetValidation();
    },

    parseSettingsData(data: TCopyStakeSettingsLimitsResponse): void {
      const parsedData = {
        streamers: String(data.streamers ?? ''),
        watchers: String(data.watchers ?? '')
      };

      this.limitsSavedData = cloneDeep(parsedData);
      this.limits = cloneDeep(parsedData);

      for (const program in this.programsSettingMap) {
        const { streamers, watchers } = this.programsSettingMap[program];

        if (
          this.limitsSavedData.streamers === `${streamers}` &&
          this.limitsSavedData.watchers === `${watchers}`
        ) {
          this.program = program;

          break;
        } else if (
          this.limitsSavedData.streamers === '' &&
          this.limitsSavedData.watchers === ''
        ) {
          this.program = null;

          break;
        } else {
          this.program = ECopyStakePackageProgram.ENTERPRISE;
        }
      }
    },

    async getConfiguration(): Promise<void> {
      if (!this.operatorId || !this.data?.streamingType) {
        return;
      }

      this.isLoading = true;

      getLimitsConfiguration(this.data?.streamingType)
        .then((data: TCopyStakeSettingsLimitsResponse) => {
          this.parseSettingsData(data);
        })
        .catch((err: AxiosError): void => {
          errorToastMessage(err);
        })
        .finally(() => {
          this.isLoading = false;
        });
    },
    async updateConfiguration(): Promise<void> {
      const isFormValid = await this.$refs.form.validate();
      if (!isFormValid || !this.data?.streamingType) return;

      this.isLoading = true;

      const payload: TCopyStakeSettingsLimitsResponse = this.limits;

      await updateLimitsConfiguration(payload, this.data?.streamingType)
        .then((data: TCopyStakeSettingsLimitsResponse) => {
          this.parseSettingsData(data);

          this.$toast.success(
            'Streamer Package limits was successfully updated!'
          );
        })
        .catch((err: AxiosError): void => {
          errorToastMessage(err);
        })
        .finally(() => {
          this.isLoading = false;
        });
    }
  }
};
